import intersection from 'lodash/intersection';
import castArray from 'lodash/castArray';
import appStore from '../mobx/appStore';
import rolesList from './roles';
import { debugLog } from '../debug/devLogger';

// Role entries straight off the signed-in user's document. Unlike getRoles()
// these are NOT scoped to the currently selected org, so they answer "what is
// this user anywhere" questions (e.g. menu/route gating before an org is
// selected).
const getUserRoleEntries = () => appStore.loggedInUser?.roles || [];

const getRoles = () => {
  const userRoleEntries = getUserRoleEntries();
  const userRoles = [
    ...new Set(userRoleEntries.map((entry) => entry?.role).filter(Boolean)),
  ];

  // Developer/Administrator roles are global and should never disappear when
  // switching selected company.
  if (userRoles.some((role) => [rolesList.dev, rolesList.admin].includes(role))) {
    return userRoles;
  }

  // Keep legacy behavior for users that are only affiliated to a single org.
  if (appStore.loggedInUser?.orgs?.length === 1) {
    return userRoles;
  }

  // For multi-org users (e.g. Org Administrators), derive scoped roles from the
  // selected org's business key (fein). Each role entry carries `fein` (the org
  // business key) alongside `org` (the org document id); scoping matches on the
  // fein per the business-key rules. `currentOrg` holds the selected fein.
  const currentFein = appStore.currentOrg;
  if (currentFein) {
    const scopedRoles = [
      ...new Set(
        userRoleEntries
          .filter((entry) => entry && entry.fein === currentFein)
          .map((entry) => entry.role)
          .filter(Boolean),
      ),
    ];
    if (scopedRoles.length > 0) {
      return scopedRoles;
    }
  }

  const record = appStore.currentOrgRecord;
  return record?.roles ? record.roles : [];
};

const hasUserRole = (roles) =>
  getUserRoleEntries().some(
    (entry) => entry && castArray(roles).includes(entry.role),
  );

// Distinct org ids where the user holds the Org Administrator role. Derived
// from the role entries (not the bare loggedInUser.orgs array, which may
// include affiliations that do not carry org-admin rights).
//
// NOTE: these are the org DOCUMENT ids (natural keys). They are used only for
// in-memory, stable-identity matching (e.g. the FEIN-change review flow, where
// the fein itself is mid-change). They are NEVER passed to a query as a key.
const orgAdminOrgIds = () => [
  ...new Set(
    getUserRoleEntries()
      .filter((entry) => entry?.role === rolesList.orgAdmin && entry?.org)
      .map((entry) => entry.org),
  ),
];

// Distinct org BUSINESS KEYS (fein) where the user holds the Org Administrator
// role. These are the keys used to SELECT companies/users on the Company Info
// page — every read routes through the fein, never a document id (see
// .claude/rules/13-no-native-ids-as-keys.md).
const orgAdminFeins = () => [
  ...new Set(
    getUserRoleEntries()
      .filter((entry) => entry?.role === rolesList.orgAdmin && entry?.fein)
      .map((entry) => entry.fein),
  ),
];

// Company Info is an Org Administrator feature; anyone holding Administrator
// manages companies through the admin listings instead and must not see it.
const canManageCompanyInfo = () =>
  hasUserRole(rolesList.orgAdmin) && !hasUserRole(rolesList.admin);

const security = {
  hasAllRoles: (roles) =>
    intersection(castArray(roles), getRoles()).length ===
    castArray(roles).length,
  hasAnyRole: (roles) =>
    getRoles().some((role) => (roles || []).includes(role)),
  // Public roles accessor — logs the resolved (org-scoped) roles. The internal
  // hasAnyRole/hasAllRoles predicates use the un-logged getRoles() to avoid
  // flooding the console on every render.
  getRoles: () => {
    const roles = getRoles();
    debugLog('roles', `getRoles (scoped to currentOrg=${appStore.currentOrg})`, roles);
    return roles;
  },
  hasUserRole,
  orgAdminOrgIds: () => {
    const ids = orgAdminOrgIds();
    debugLog('roles', 'orgAdminOrgIds (document ids)', ids);
    return ids;
  },
  orgAdminFeins: () => {
    const feins = orgAdminFeins();
    debugLog('roles', 'orgAdminFeins (business keys)', feins);
    return feins;
  },
  canManageCompanyInfo,
};

export default security;
